import { useState } from 'react';
import { Modal, ModalFooter } from '../../ui/Modal';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { WEEKLY_ROLES } from '../../../types/meetings';

interface LogAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: string;
  meetingName: string;
  onSaved: () => void;
  onLogAttendance: (data: {
    meetingId: string;
    meetingName: string;
    attendedDate: string;
    weeklyRole?: string;
    notes?: string;
  }) => Promise<void>;
}

export function LogAttendanceModal({
  isOpen,
  onClose,
  meetingId,
  meetingName,
  onSaved,
  onLogAttendance,
}: LogAttendanceModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const [attendedDate, setAttendedDate] = useState(today);
  const [weeklyRole, setWeeklyRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  function resetForm() {
    setAttendedDate(today);
    setWeeklyRole('');
    setCustomRole('');
    setNotes('');
  }

  async function handleSave() {
    setSaving(true);
    try {
      const resolvedRole = weeklyRole === 'Other' ? customRole.trim() : weeklyRole;
      await onLogAttendance({
        meetingId,
        meetingName,
        attendedDate,
        ...(resolvedRole ? { weeklyRole: resolvedRole } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      resetForm();
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Attendance"
      description={meetingName}
      size="sm"
      closeOnOverlayClick={false}
    >
      <div className="space-y-4">
        {/* Date */}
        <Input
          label="Date attended"
          type="date"
          value={attendedDate}
          onChange={e => setAttendedDate(e.target.value)}
          max={today}
        />

        {/* Weekly role */}
        <Select
          label="Service role (optional)"
          value={weeklyRole}
          onChange={e => setWeeklyRole(e.target.value)}
          options={WEEKLY_ROLES.map(r => ({ value: r, label: r }))}
          placeholder="None"
        />

        {/* Free text if "Other" selected */}
        {weeklyRole === 'Other' && (
          <Input
            label="Custom role"
            placeholder="Describe your role…"
            value={customRole}
            onChange={e => setCustomRole(e.target.value)}
          />
        )}

        {/* Notes */}
        <Textarea
          label="Notes (optional)"
          placeholder="Any notes about this meeting…"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
        />
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} isLoading={saving}>
          Log Attendance
        </Button>
      </ModalFooter>
    </Modal>
  );
}
